import { XsI18nPipe } from '@nx-fe/pipes';
import { FeatureToggleService, hd } from '@nx-fe/services';
import { ShortDateFormatPipe } from 'app/shared/pipes/time-format.pipe';
import { FileData } from './fileData';
import { IHtmlTreeDelegate } from './htmlTree';
import { Tooltip } from './tooltip';

type IFilterService = hd.IFilterService;

export class TreeNode {
  public id: number;
  public title: string;
  public tooltipText: string;
  public nodes: TreeNode[];
  public transferNodes?: TreeNode[];
  public parent: TreeNode;
  public data: FileData;
  public level: number;
  public type: string;
  public collapsed: boolean;
  public isDownloading: boolean;
  public isUploading: boolean;
  public percentValue: number;

  public static cloneForTransfer = (node: TreeNode): TreeNode => {
    const copy = new TreeNode();
    copy.id = node.id;
    copy.title = node.title;
    copy.tooltipText = node.tooltipText;
    copy.level = node.level;
    copy.type = node.type;
    copy.collapsed = node.collapsed;
    copy.isDownloading = false;
    copy.isUploading = false;
    copy.percentValue = 0;

    if (node.data) {
      copy.data = FileData.clone(node.data);
    }

    // Clone child nodes and set parent
    if (node.nodes) {
      copy.nodes = [];
      for (const childNode of node.nodes) {
        const childNodeCopy = TreeNode.cloneForTransfer(childNode);
        childNodeCopy.parent = copy;
        copy.nodes.push(childNodeCopy);
      }
    }

    return copy;
  };

  /** Searches for a node with the given id recursively within this tree node */
  public static findNodeById = (node: TreeNode, id: number): TreeNode => {
    if (node.id === id) {
      return node;
    }

    if (node.nodes) {
      for (const child of node.nodes) {
        const match = TreeNode.findNodeById(child, id);
        if (match) {
          return match;
        }
      }
    }

    return null;
  };

  public static findNode = (root: TreeNode, node: TreeNode): TreeNode => {
    if (!root || !node) {
      return undefined;
    }

    let nodePoint = node;
    let rootPoint = root;
    const branch: TreeNode[] = [nodePoint];

    while (nodePoint.parent) {
      branch.unshift(nodePoint.parent);
      nodePoint = nodePoint.parent;
    }

    for (let i = 1; i < branch.length; i++) {
      if (rootPoint) {
        rootPoint = rootPoint.nodes.find((rootItem: TreeNode) => rootItem.title == branch[i].title);
      } else {
        //didn't find
        return undefined;
      }
    }

    return rootPoint;
  };
}

export class HtmlTreeNode {
  public rowNode: HTMLElement; // table row containing this file node

  public childNodes: HTMLElement; // Contains child row nodes
  public childNodesTable: HTMLElement; // Nested table that child nodes are added directly to

  public tooltip: Tooltip;
  public title: string;

  public namePrefixNode: HTMLElement; // Prefix node contains the "Twisty" and the icon
  public nameTextWrapper: HTMLElement; // Node that wraps the name text element
  public nameTextNode: HTMLElement; // Contains filename text
  public nameTextTitle: HTMLElement; // Contains filename text

  private fileTable: HTMLElement;

  private nameTwistyNode: HTMLElement; // "Twisty" for expanding folder
  private nameIconNode: HTMLElement; // Display icon (drive, folder, file)

  private isLoadingVisible = false;
  private loadingNode: HTMLElement; // Node for displaying "Loading..."

  constructor(
    public node: TreeNode,
    public alwaysTooltip: boolean,
    private fileExplorerState: IHtmlTreeDelegate,
    private saI18nPipe: XsI18nPipe,
    private dateFormatPipe: ShortDateFormatPipe,
    private featureToggleService: FeatureToggleService
  ) {
    this.rowNode = document.createElement('tr');
    this.rowNode.className = 'tree-node node-highlight max-height';
    this.title = node.title;

    this.rowNode.id = 'tr' + node.id;
    const currentDeep =
      node.level - this.fileExplorerState.breadCrumbNodes.length === -1
        ? this.fileExplorerState.breadCrumbNodes.length - 1
        : 0;

    let featureNameTextHtml = '<div class="display-table-cell fixed tree-node name-text-node"></div>';
    let shiftForTitle = 0;
    if (
      this.featureToggleService.isEnabled('FileManagerActionBarRenameDownloadDelete') ||
      this.featureToggleService.isEnabled('FileManagerActionBarCopyAttachCompressMove')
    ) {
      featureNameTextHtml =
        '<div class="display-table-cell fixed tree-node name-text-node"><div class="name-text-title"></div></div>';
      shiftForTitle = 4;
    }

    // Set template for file row columns
    this.rowNode.innerHTML =
      '<td class="tree-node max-height cell-left">' +
      '<div class="display-table fill">' +
      '<div class="display-table-cell condensed" style="padding-left:' +
      ((node.level - currentDeep) * 12 - shiftForTitle) +
      'px">' +
      '<div class="display-table tree-node name-prefix-node">' +
      (node.type === 'folder' || node.type === 'drive'
        ? '<div class="fa fa-angle-right display-table-cell condensed tree-node name-twisty-node"></div>'
        : '') +
      '<div class="display-table-cell condensed tree-node name-icon-node ' +
      HtmlTreeNode.getNodeIconClass(node.type) +
      '"></div>' +
      '</div>' +
      '</div>' +
      '<div class="display-table-cell tree-node name-text-wrapper">' +
      '<div class="display-table fill">' +
      featureNameTextHtml +
      '</div>' +
      '</div>' +
      '</div>' +
      '</td>' +
      '<td class="tree-node max-height cell-right file-size">' +
      this.getFileSize(node) +
      '</td>' +
      '<td class="tree-node max-height cell-right file-type">' +
      this.getFileType(node) +
      '</td>' +
      '<td class="tree-node max-height cell-right file-date">' +
      (node.data && node.data.formattedDate ? this.dateFormatPipe.transform(node.data.date.toString(), true) : '') +
      '</td>';

    this.namePrefixNode = this.rowNode.getElementsByClassName('name-prefix-node')[0] as HTMLElement;
    this.nameTwistyNode =
      node.type === 'folder' || node.type === 'drive'
        ? (this.rowNode.getElementsByClassName('name-twisty-node')[0] as HTMLElement)
        : null;
    this.nameIconNode = this.rowNode.getElementsByClassName('name-icon-node')[0] as HTMLElement;
    this.nameTextWrapper = this.rowNode.getElementsByClassName('name-text-wrapper')[0] as HTMLElement;
    this.nameTextNode = this.rowNode.getElementsByClassName('name-text-node')[0] as HTMLElement;

    if (
      this.featureToggleService.isEnabled('FileManagerActionBarRenameDownloadDelete') ||
      this.featureToggleService.isEnabled('FileManagerActionBarCopyAttachCompressMove')
    ) {
      this.nameTextTitle = this.rowNode.getElementsByClassName('name-text-title')[0] as HTMLElement;

      this.nameTextTitle.innerText = node.title;
    } else {
      this.nameTextNode.innerText = node.title;
    }

    // Nested table to hold child file row elements
    this.childNodes = document.createElement('tr');
    this.childNodes.innerHTML = '<td colspan="4">' + '<table class="tree-node child-nodes">' + '</table>' + '</td>';
    this.childNodesTable = this.childNodes.getElementsByClassName('child-nodes')[0] as HTMLElement;

    this.loadingNode = document.createElement('div');
    this.loadingNode.className = 'display-table-cell tree-node node';
    const loadingIconSpan = document.createElement('span');
    const loadingTextSpan = document.createElement('span');
    loadingIconSpan.className = 'fa fa-spinner fa-pulse fa-fw';
    loadingTextSpan.innerText = this.saI18nPipe.transform('fileExplorer_Loading');
    this.loadingNode.appendChild(loadingIconSpan);
    this.loadingNode.appendChild(loadingTextSpan);
    this.fileTable = document.getElementById('filetable');
    this.tooltip = new Tooltip(
      this.nameTextWrapper,
      this.nameTextNode,
      this.fileTable,
      node.tooltipText,
      this.alwaysTooltip
    );
    this.nameTextWrapper.style.position = 'relative';
  }

  private static getNodeIconClass = (nodeType: string): string => {
    switch (nodeType) {
      case 'folder':
        return 'fa fa-folder icon-padding-folder-closed';
      case 'drive':
        return 'fa hd-icon hd-icon-drive icon-padding-folder-closed';
      default:
        return 'fa fa-file icon-padding-file';
    }
  };

  public getFileSize = (node: TreeNode): string => (node.type === 'file' ? node.data.fileSize : '');

  public getFileType = (node: TreeNode): string => {
    switch (node.type) {
      case 'file':
        return this.saI18nPipe.transform('fileExplorer_File');
      case 'folder':
        return this.saI18nPipe.transform('fileExplorer_Folder');
      default:
        return this.saI18nPipe.transform('fileExplorer_Other');
    }
  };

  public childNodesVisible = (): boolean =>
    this.childNodes.style.display !== 'none' && this.childNodes.style.display !== '';

  public select = () => {
    this.rowNode.classList.add('tree-node-selected');
  };

  public unSelect = () => {
    this.rowNode.classList.remove('tree-node-selected');
  };

  public expandNode = () => {
    this.node.collapsed = false;
    this.showChildNodes();
  };

  public collapseNode = () => {
    this.node.collapsed = true;
    this.hideChildNodes();
  };

  public toggleNode = () => {
    if (this.childNodesVisible()) {
      this.collapseNode();
    } else {
      this.expandNode();
    }
  };

  public addLoadingNode = () => {
    this.isLoadingVisible = true;
    this.childNodesTable.insertBefore(this.loadingNode, this.childNodesTable.childNodes[0]);
    this.loadingNode.style.paddingLeft = (this.node.level + this.fileExplorerState.breadCrumbNodes.length) * 12 + 'px';
  };

  public removeLoadingNode = () => {
    this.isLoadingVisible = false;
    try {
      this.childNodesTable.removeChild(this.loadingNode);
    } catch (e) {}
  };

  public isLoading = () => this.isLoadingVisible;

  public getParent = (): TreeNode => this.node.parent;

  public showChildNodes = () => {
    this.shiftTable();
    if (!this.childNodes.parentNode) {
      this.rowNode.insertAdjacentElement('afterend', this.childNodes);
    }
    this.childNodes.style.display = 'table-row';
  };

  /** shift the child table to the left to remove the leading space  */
  private shiftTable = () => {
    //Turn off feature because https://jira.soti.net/browse/HD-6188
    if (
      this.featureToggleService.isEnabled('FileManagerActionBarRenameDownloadDelete') ||
      this.featureToggleService.isEnabled('FileManagerActionBarCopyAttachCompressMove')
    ) {
      return;
    }

    // check if the current node of if the last element of the breadCrumbNodes
    // only shift the table if it is the last element
    if (this.node.level - this.fileExplorerState.breadCrumbNodes.length === -1) {
      const offset: number = (this.fileExplorerState.breadCrumbNodes.length - 1) * 12;
      // add negative margin of the table shift to the left to remove the leading space of the node
      this.childNodesTable.style.marginLeft = -1 * offset + 'px';

      // expand the child table to make it just fit the fileExplorer
      // Avoiding the file attributes is not in the correct position
      this.expandChildTableNode();
    } else {
      // other wise reset the width and left margin to default
      this.childNodesTable.style.marginLeft = 0 + 'px';
      this.childNodesTable.style.width = '100%';
    }
  };

  /** Expand the childNodesTable width with the position the table has shifted  */
  private expandChildTableNode = () => {
    if (!this.fileTable) {
      return;
    }

    const offset: number = (this.fileExplorerState.breadCrumbNodes.length - 1) * 12;
    const width = this.fileTable.offsetWidth;
    let actualWidth: number = ((width + offset) / width) * 100;

    // Clamp width to 100% to prevent overflow (widths > 100%) from causing scrolling unintentionally
    actualWidth = Math.min(actualWidth, 100);

    this.childNodesTable.style.width = actualWidth + '%';
  };

  private hideChildNodes = () => {
    this.childNodes.style.display = 'none';
  };
}
